const JOURNAL_PREFIX = 'diko-credential-state:v1:'
const CLAIM_PREFIX = `${JOURNAL_PREFIX}claim:`
const SNAPSHOT_PREFIX = `${JOURNAL_PREFIX}snapshot:`
const LOCK_NAME = 'diko-credential-state'
const LEASE_KEY = `${JOURNAL_PREFIX}lease`
const LEASE_MS = 1000
const LEASE_RENEW_MS = Math.floor(LEASE_MS / 3)
const MAX_SAFE_FENCE = Number.MAX_SAFE_INTEGER - 1
const RETAINED_SNAPSHOTS = 48

const isBrowser = () => typeof window !== 'undefined' && typeof localStorage !== 'undefined'

export class CredentialStateError extends Error {
  constructor(message) {
    super(message)
    this.name = 'CredentialStateError'
  }
}

const isSafeInteger = value => Number.isSafeInteger(value) && value >= 0

const isNonce = value => typeof value === 'string' && /^[A-Za-z0-9_-]{43}$/.test(value)

const encodeBytes = bytes => {
  let binary = ''
  bytes.forEach(byte => { binary += String.fromCharCode(byte) })
  return window.btoa(binary).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')
}

export const randomBase64Url = () => {
  if (!isBrowser() || !window.crypto || typeof window.crypto.getRandomValues !== 'function') {
    throw new CredentialStateError('Secure browser randomness is unavailable')
  }
  const bytes = new Uint8Array(32)
  window.crypto.getRandomValues(bytes)
  return encodeBytes(bytes)
}

const tokenKey = token => `${String(token.fence).padStart(16, '0')}:${token.ownerNonce}`

const compareTokens = (left, right) => {
  if (!left && !right) return 0
  if (!left) return -1
  if (!right) return 1
  if (left.fence !== right.fence) return left.fence - right.fence
  return left.ownerNonce.localeCompare(right.ownerNonce)
}

const parseToken = value => {
  if (!value || !isSafeInteger(value.fence) || !isNonce(value.ownerNonce)) {
    throw new CredentialStateError('Malformed credential-state fencing token')
  }
  return { fence: value.fence, ownerNonce: value.ownerNonce }
}

const initialState = () => ({
  version: 1,
  browserFlowId: null,
  loginIntent: null,
  accepted: null,
  credentialAuthorityRevision: 0,
  factor: null,
  tombstones: {},
})

const parseRecord = (storage, key) => {
  let value
  try {
    value = JSON.parse(storage.getItem(key))
  } catch (error) {
    throw new CredentialStateError('Credential-state journal contains malformed JSON')
  }
  if (!value || value.version !== 1) {
    throw new CredentialStateError('Credential-state journal version is invalid')
  }
  return value
}

const listKeys = (storage, prefix) => {
  const keys = []
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (key && key.startsWith(prefix)) keys.push(key)
  }
  return keys
}

const greatestRecord = (storage, prefix) => {
  let selected = null
  listKeys(storage, prefix).forEach(key => {
    const record = parseRecord(storage, key)
    const token = parseToken(record.token)
    if (!selected || compareTokens(token, selected.token) > 0) selected = { key, record, token }
  })
  return selected
}

// CODE-R1-010: the authoritative "am I still the writer allowed to run
// irreversible side effects" question is answered by the append-only
// journal's own greatest fencing token, never by whether a coordination
// lease still looks unexpired -- a lease is only a best-effort optimization
// to avoid wasted concurrent work, not a source of truth.
const stillGreatestSnapshot = token => {
  const greatest = greatestRecord(localStorage, SNAPSHOT_PREFIX)
  return Boolean(greatest && compareTokens(greatest.token, token) === 0)
}

export const readCredentialState = () => {
  if (!isBrowser()) return { state: initialState(), token: null }
  const snapshot = greatestRecord(localStorage, SNAPSHOT_PREFIX)
  if (!snapshot) return { state: initialState(), token: null }
  if (!snapshot.record.state || snapshot.record.state.version !== 1) {
    throw new CredentialStateError('Credential-state snapshot is malformed')
  }
  return { state: snapshot.record.state, token: snapshot.token }
}

const appendRecord = (storage, prefix, token, value) => {
  const key = `${prefix}${tokenKey(token)}`
  if (storage.getItem(key) !== null) throw new CredentialStateError('Credential-state journal key collision')
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch (error) {
    throw new CredentialStateError('Credential-state journal write failed')
  }
  if (storage.getItem(key) === null) throw new CredentialStateError('Credential-state journal write was not durable')
  return key
}

const claimFence = () => {
  const greatestClaim = greatestRecord(localStorage, CLAIM_PREFIX)
  const greatestSnapshot = greatestRecord(localStorage, SNAPSHOT_PREFIX)
  const maxFence = Math.max(
    greatestClaim ? greatestClaim.token.fence : 0,
    greatestSnapshot ? greatestSnapshot.token.fence : 0,
  )
  if (!isSafeInteger(maxFence) || maxFence >= MAX_SAFE_FENCE) {
    throw new CredentialStateError('Credential-state fence is exhausted')
  }
  const token = { fence: maxFence + 1, ownerNonce: randomBase64Url() }
  appendRecord(localStorage, CLAIM_PREFIX, token, { version: 1, token })
  const greatest = greatestRecord(localStorage, CLAIM_PREFIX)
  if (!greatest || compareTokens(greatest.token, token) !== 0) {
    throw new CredentialStateError('Credential-state fence was superseded')
  }
  return token
}

const ownsGreatestClaim = token => {
  const greatest = greatestRecord(localStorage, CLAIM_PREFIX)
  return Boolean(greatest && compareTokens(greatest.token, token) === 0)
}

const gcJournal = selectedToken => {
  const snapshots = listKeys(localStorage, SNAPSHOT_PREFIX)
    .map(key => {
      const record = parseRecord(localStorage, key)
      return { key, token: parseToken(record.token) }
    })
    .sort((left, right) => compareTokens(right.token, left.token))
  const retained = new Set(snapshots.slice(0, RETAINED_SNAPSHOTS).map(item => item.key))
  snapshots.slice(RETAINED_SNAPSHOTS).forEach(item => {
    if (compareTokens(item.token, selectedToken) < 0 && !retained.has(item.key)) {
      localStorage.removeItem(item.key)
    }
  })
  listKeys(localStorage, CLAIM_PREFIX).forEach(key => {
    const record = parseRecord(localStorage, key)
    if (compareTokens(parseToken(record.token), selectedToken) < 0) localStorage.removeItem(key)
  })
}

const fencedTransition = transition => {
  if (!isBrowser()) throw new CredentialStateError('Credential-state fencing requires a browser')
  const token = claimFence()
  const current = readCredentialState()
  const transitionResult = transition(current.state, token)
  const nextState = transitionResult && transitionResult.state
    ? transitionResult.state
    : transitionResult
  const value = transitionResult && transitionResult.state
    ? transitionResult.value
    : undefined
  if (!nextState || nextState.version !== 1) {
    throw new CredentialStateError('Credential-state transition returned an invalid state')
  }
  if (!ownsGreatestClaim(token)) throw new CredentialStateError('Credential-state transition lost its fence')
  appendRecord(localStorage, SNAPSHOT_PREFIX, token, { version: 1, token, state: nextState })
  const selected = readCredentialState()
  if (compareTokens(selected.token, token) !== 0) {
    return {
      committed: false, state: selected.state, token: selected.token, value: undefined,
    }
  }
  gcJournal(token)
  return {
    committed: true, state: selected.state, token, value,
  }
}

const readLease = () => {
  const raw = localStorage.getItem(LEASE_KEY)
  if (!raw) return null
  try {
    const lease = JSON.parse(raw)
    if (!isNonce(lease.owner) || !isSafeInteger(lease.expiresAt)) throw new Error('invalid lease')
    return lease
  } catch (error) {
    throw new CredentialStateError('Credential-state coordination lease is malformed')
  }
}

const renewLease = owner => {
  try {
    const current = readLease()
    if (!current || current.owner !== owner) return
    localStorage.setItem(LEASE_KEY, JSON.stringify({ owner, expiresAt: Date.now() + LEASE_MS }))
  } catch (error) {
    // Best-effort: a transient malformed read must not throw inside the
    // heartbeat timer. Missing a single renewal tick is recoverable as long
    // as a later tick succeeds before the lease's remaining grant elapses.
  }
}

const withStorageLease = async task => {
  const owner = randomBase64Url()
  const deadline = Date.now() + LEASE_MS * 2
  let acquired = false
  while (!acquired) {
    const now = Date.now()
    const existing = readLease()
    if (!existing || existing.expiresAt <= now || existing.owner === owner) {
      localStorage.setItem(LEASE_KEY, JSON.stringify({ owner, expiresAt: now + LEASE_MS }))
      const selected = readLease()
      acquired = Boolean(selected && selected.owner === owner)
    }
    if (!acquired) {
      if (Date.now() >= deadline) throw new CredentialStateError('Credential-state coordination lease is unavailable')
      const remaining = Math.max(1, Math.min(25, existing.expiresAt - now))
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, remaining))
    }
  }
  // CODE-R1-010: `task` may hold the lease through an awaited install
  // callback (see withCredentialStateFence below) for longer than the
  // original one-second grant. Without a heartbeat renewing it well inside
  // that window, a still-active writer could lose exclusivity to a
  // concurrent writer that only *looks* free because the lease's clock ran
  // out, not because the original holder actually finished.
  const heartbeat = setInterval(() => renewLease(owner), LEASE_RENEW_MS)
  try {
    return await task()
  } finally {
    clearInterval(heartbeat)
    const selected = readLease()
    if (selected && selected.owner === owner) localStorage.removeItem(LEASE_KEY)
  }
}

const installCallbackPlan = installResult => {
  if (typeof installResult === 'function') return { commit: installResult, rollback: null }
  if (!installResult || typeof installResult !== 'object') return {}
  return {
    commit: typeof installResult.commit === 'function' ? installResult.commit : null,
    rollback: typeof installResult.rollback === 'function' ? installResult.rollback : null,
    finalize: typeof installResult.finalize === 'function' ? installResult.finalize : null,
  }
}

// CODE-R1-010: `install`, when given, runs after a successful commit but
// while the fence's lock/lease is still held -- so no presentation, token,
// broadcast, or auth event side effect can ever run after a greater
// cross-tab writer has had a chance to install a newer authority. Ownership
// proven at commit time remains valid for as long as the lock is held, which
// now spans the side effects themselves, not just the state transition.
export const withCredentialStateFence = async (transition, install) => {
  const run = async () => {
    const result = fencedTransition(transition)
    if (result.committed && install) {
      const plan = installCallbackPlan(await install(result.state, result.token, result.value))
      // CODE-R1-010: re-verify against the journal itself immediately
      // before running irreversible side effects; this is the final gate,
      // independent of lease timing.
      if (!stillGreatestSnapshot(result.token)) {
        const superseded = readCredentialState()
        return {
          committed: false, state: superseded.state, token: superseded.token, value: undefined,
        }
      }

      let { rollback } = plan
      if (plan.commit) {
        try {
          const dynamicRollback = await plan.commit()
          if (typeof dynamicRollback === 'function') rollback = dynamicRollback
        } catch (error) {
          if (rollback) await rollback()
          throw error
        }
      }
      // A process can be frozen after the pre-commit check while another tab
      // steals the expired fallback lease. Re-check after reversible writes;
      // a loser compensates those writes and never reaches notifications.
      if (!stillGreatestSnapshot(result.token)) {
        if (rollback) await rollback()
        const superseded = readCredentialState()
        return {
          committed: false, state: superseded.state, token: superseded.token, value: undefined,
        }
      }
      if (plan.finalize) {
        // A finalizer may prepare asynchronously before emitting its first
        // irreversible notification. Give it a live journal predicate and
        // require it to decline the notification if this writer was
        // superseded during that preparation window.
        const finalized = await plan.finalize(() => stillGreatestSnapshot(result.token))
        if (finalized === false) {
          if (rollback) await rollback()
          const superseded = readCredentialState()
          return {
            committed: false, state: superseded.state, token: superseded.token, value: undefined,
          }
        }
      }
    }
    return result
  }
  if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
    return navigator.locks.request(LOCK_NAME, { mode: 'exclusive' }, run)
  }
  return withStorageLease(run)
}

export const compareAuthority = (left, right) => {
  if (!left && !right) return 0
  if (!left) return -1
  if (!right) return 1
  if (left.ae !== right.ae) return left.ae - right.ae
  return left.se - right.se
}

export const compareCredential = (left, right) => {
  const authority = compareAuthority(left, right)
  if (authority !== 0) return authority
  return left.rv - right.rv
}

export const sameAuthorityId = (left, right) => Boolean(
  left && right && String(left.userId) === String(right.userId) && String(left.sid) === String(right.sid),
)

export const authorityIdFor = tuple => `${tuple.userId}:${tuple.sid}`

export const snapshotJournalKeys = () => (isBrowser() ? listKeys(localStorage, SNAPSHOT_PREFIX) : [])
